import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { Star, ArrowRight, Check, Users, Calendar, DollarSign } from "lucide-react";
import dashboardImg from "@/assets/dashboard-screenshot.png";

const LandingOption4 = () => {
  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "PTA Chair",
      content: "PTA Hub has transformed how we manage our school community. What used to take hours now takes minutes.",
      rating: 5,
    },
    {
      name: "Michael Davies",
      role: "Treasurer",
      content: "Having all our financial tracking in one place has been incredible. The fundraising tools make my role so much easier.",
      rating: 5,
    },
    {
      name: "Emma Wilson",
      role: "Secretary",
      content: "Our event attendance has increased by 40% since we started using PTA Hub. The communication features are fantastic.",
      rating: 5,
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Trust Signals Hero */}
      <section className="relative bg-gradient-hero py-20 px-4">
        <div className="container mx-auto max-w-5xl text-center space-y-8 animate-fade-in">
          <Badge className="px-6 py-2 text-base">
            Trusted by 500+ PTAs Across the UK
          </Badge>
          <h1 className="text-5xl md:text-7xl font-bold text-foreground leading-tight">
            Join Britain's Leading
            <span className="block bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              PTA Platform
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">
            Discover why PTA leaders across the country choose PTA Hub to manage their communities
          </p>
          <div className="flex items-center justify-center gap-2 pt-4">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-8 w-8 fill-primary text-primary" />
            ))}
            <span className="text-xl text-foreground ml-2 font-semibold">4.9/5 from 200+ reviews</span>
          </div>
        </div>
      </section>

      {/* Testimonials - Featured */}
      <section className="py-16 px-4 bg-card">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card 
                key={index} 
                className="shadow-card hover:shadow-elevated transition-all border-2"
              >
                <CardHeader>
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-primary text-primary" />
                    ))}
                  </div>
                  <CardDescription className="text-base leading-relaxed text-foreground">
                    "{testimonial.content}"
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="border-t border-border pt-4">
                    <div className="font-semibold text-foreground">{testimonial.name}</div>
                    <div className="text-sm text-muted-foreground">{testimonial.role}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground">
              Everything Your PTA Needs
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Professional tools trusted by PTA leaders nationwide
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-primary flex items-center justify-center shrink-0">
                  <Users className="h-6 w-6 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">Member Management</h3>
                  <p className="text-muted-foreground">
                    Efficiently organize your PTA members, roles, and permissions in one central hub
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-primary flex items-center justify-center shrink-0">
                  <Calendar className="h-6 w-6 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">Event Planning</h3>
                  <p className="text-muted-foreground">
                    Coordinate events seamlessly with intuitive calendar tools and RSVP tracking
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-primary flex items-center justify-center shrink-0">
                  <DollarSign className="h-6 w-6 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">Fundraising</h3>
                  <p className="text-muted-foreground">
                    Track donations and manage campaigns with transparent financial tools
                  </p>
                </div>
              </div>
            </div>
            <div>
              <Card className="overflow-hidden shadow-elevated">
                <img src={dashboardImg} alt="PTA Hub Dashboard" className="w-full" />
              </Card>
            </div>
          </div>

          <div className="bg-card/50 rounded-3xl p-12">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div className="space-y-2">
                <div className="text-5xl font-bold text-primary">98%</div>
                <div className="text-lg text-foreground font-semibold">Customer Satisfaction</div>
              </div>
              <div className="space-y-2">
                <div className="text-5xl font-bold text-primary">10+</div>
                <div className="text-lg text-foreground font-semibold">Hours Saved Weekly</div>
              </div>
              <div className="space-y-2">
                <div className="text-5xl font-bold text-primary">24/7</div>
                <div className="text-lg text-foreground font-semibold">Support Available</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 px-4 bg-card/30">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground">
              Why PTAs Choose Us
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Save 10+ hours per week on admin tasks",
              "Increase parent engagement by up to 50%",
              "GDPR compliant and secure",
              "Streamline fundraising campaigns",
              "Improve financial transparency",
              "Dedicated support team"
            ].map((benefit, i) => (
              <div key={i} className="flex items-center gap-4 bg-card p-6 rounded-xl border-2 border-border">
                <Check className="h-7 w-7 text-primary shrink-0" />
                <span className="text-lg text-foreground">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 bg-gradient-hero">
        <div className="container mx-auto max-w-4xl text-center space-y-8">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground">
            Ready to Join 500+ PTAs?
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Start your free 14-day trial today. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/auth">
              <Button size="lg" className="shadow-elevated text-xl px-12 py-8">
                Start Free Trial
                <ArrowRight className="ml-2 h-6 w-6" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="text-xl px-12 py-8">
                Contact Sales
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Label */}
      <div className="fixed top-20 right-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg shadow-lg font-bold z-50">
        Option 4
      </div>
    </div>
  );
};

export default LandingOption4;
